import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useBrand } from '../contexts/BrandContext';
import { TIERS } from '../utils/planUtils';

/**
 * Blocks a locked (unpaid) account, but deliberately NOT with a dead end.
 *
 * The product has two billing states — trial and paid — plus locked. When locked we stop
 * the app but keep three escape hatches open, because a contractor who cannot reach their
 * own billing page, log out, or see their data has no route back to paying us:
 *   • /settings (the Payments tab holds SubscriptionManager — where they actually pay)
 *   • /logout
 *   • read-only viewing is NOT granted here; that is a larger change, tracked separately.
 *
 * Role-aware: only owner/admin can act on billing. A technician hitting this screen cannot
 * pay and should be told to contact their administrator, not asked for a card.
 *
 * `grace` is intentionally NOT blocked — it renders a warning banner and full access while
 * Stripe retries the card (see GRACE_PERIOD_DAYS in planUtils).
 */

// Paths that must stay reachable while locked, or there is no way to recover the account.
const ALWAYS_ALLOWED = ['/settings', '/logout'];

const BILLING_ROLES = ['OWNER', 'ADMIN'];

export const BillingBanner = () => {
  const { user } = useUser();
  const billing = user?.billing;
  if (!billing) return null;

  const canPay = BILLING_ROLES.includes(user?.role);
  const days = billing.daysRemaining;

  if (billing.state === 'grace') {
    return (
      <div className="bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800 px-4 py-2 text-sm text-amber-900 dark:text-amber-200">
        <strong>Payment failed.</strong>{' '}
        {days > 0 ? `Your account stays active for ${days} more day${days === 1 ? '' : 's'} while we retry.` : 'We are retrying your payment.'}{' '}
        {canPay && <Link to="/settings?tab=billing" className="underline font-medium">Update payment method</Link>}
      </div>
    );
  }

  // Nudge near the end of a trial (only when we actually know the end date).
  if (billing.state === 'trial' && days !== null && days <= 7) {
    return (
      <div className="bg-blue-50 dark:bg-blue-900/30 border-b border-blue-200 dark:border-blue-800 px-4 py-2 text-sm text-blue-900 dark:text-blue-200">
        {days > 0
          ? <>Your free trial ends in <strong>{days} day{days === 1 ? '' : 's'}</strong>.</>
          : <>Your free trial ends <strong>today</strong>.</>}{' '}
        {canPay && <Link to="/settings?tab=billing" className="underline font-medium">Set up billing</Link>}
      </div>
    );
  }

  return null;
};

const LockedScreen = ({ reason, canPay }) => {
  // Brand-resolved: an SMBMate company being locked out was thanked for trying TradesMatePro.
  const { brand } = useBrand();
  const brandName = brand?.name || 'TradesMatePro';
  // `reason` is the billing_status that locked the account (planUtils). Only the payment
  // statuses may say a payment failed — a CANCELLED subscription was told "we were not able
  // to process your most recent payment" while the billing page said "Status: Canceled".
  const cancelled = reason === 'canceled' || reason === 'cancelled';
  const paymentProblem = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired'].includes(reason);
  const headline = reason === 'trial_expired'
    ? 'Your free trial has ended'
    : cancelled ? 'Your subscription has ended' : 'Your subscription needs attention';
  const detail = reason === 'trial_expired'
    ? `Thanks for trying ${brandName}. Start a subscription to pick up right where you left off — your data is safe and waiting.`
    : cancelled
      ? 'Your subscription was cancelled, so the account is paused. Your data is safe and nothing has been deleted — resubscribe to pick up where you left off.'
      : paymentProblem
        ? 'We were not able to process your most recent payment, so your account is paused. Your data is safe and nothing has been deleted.'
        : 'Your subscription is not active, so your account is paused. Your data is safe and nothing has been deleted.';

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-slate-900 px-4">
      <div className="max-w-md w-full bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 p-8 text-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-3">{headline}</h1>
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">{detail}</p>

        {canPay ? (
          <Link
            to="/settings?tab=billing"
            className="inline-flex items-center justify-center w-full px-6 py-3 rounded-xl bg-green-600 hover:bg-green-700 text-white font-semibold transition-colors"
          >
            Reactivate my account
          </Link>
        ) : (
          <p className="text-sm text-gray-700 dark:text-gray-200 bg-gray-50 dark:bg-slate-700/50 rounded-lg px-4 py-3">
            Please ask your account owner or an administrator to update the company&apos;s billing details.
          </p>
        )}

        <Link to="/logout" className="block mt-4 text-sm text-gray-500 dark:text-gray-400 hover:underline">
          Sign out
        </Link>
      </div>
    </div>
  );
};

const BillingGate = ({ children }) => {
  const { user } = useUser();
  const location = useLocation();

  // Absent billing info (still loading, or a failed lookup) must never lock anyone out.
  if (!user?.billing || user.billing.tier !== TIERS.LOCKED) return children;

  if (ALWAYS_ALLOWED.some((p) => location.pathname.startsWith(p))) return children;

  return <LockedScreen reason={user.billing.reason} canPay={BILLING_ROLES.includes(user?.role)} />;
};

export default BillingGate;
